/* Copyright (c) 2023 CLOUDPILOTS Software & Consulting GmbH */

import { useCaba } from '@cloudpilots/components';
import { useEffect, useState } from 'react';
import firebaseAuth from 'services/firebase/auth';
import firebase from 'services/firebase/firebase';
import Swagger from 'swagger-client';
import { api as apiConfig, scopes as configPermissions } from '../../config';

const evaluatePermissions = (authInfo) => {
  const { isAdmin, permissions } = authInfo.claims;

  const newScopes = Object.keys(configPermissions).reduce((acc, key) => {
    const permission = configPermissions[key];
    const type = typeof permission;

    let hasAdmin;
    if (type === 'string')
      hasAdmin = !!Object.keys(permissions).find(
        (pId) => pId === configPermissions[key],
      );

    if (type === 'boolean') hasAdmin = configPermissions[key];

    if (Array.isArray(permission)) {
      hasAdmin = permission
        .map((scope) => Object.keys(permissions).map((pId) => pId === scope))
        .flat()
        .includes(true);
    }

    return {
      ...acc,
      [key]: isAdmin || hasAdmin,
    };
  }, {});

  newScopes.isAdmin = isAdmin;

  return newScopes;
};

const initFirebase = async (client) => {
  try {
    const { token } = (await client.apis.permissions.token()).obj;
    const authResult = await firebaseAuth.signInWithCustomToken(token);
    const auth = await authResult.user.getIdTokenResult();
    auth.claims.permissions = evaluatePermissions(auth);
    window.firebase = firebase;
    return {
      loaded: true,
      ...auth,
    };
  } catch (error) {
    console.error('Firebase error', error);
    window.firebase && delete window.firebase;
    return {
      loaded: false,
    };
  }
};

const prepareClient = async (client) => {
  const cabaClient = { ...client };
  cabaClient.auth = await initFirebase(client);
  const { auth } = cabaClient;
  const didLoad = auth.loaded;
  cabaClient.getCustomer = () => (didLoad ? auth.claims.customer : null);
  cabaClient.myPermissions = () => (didLoad ? auth.claims.permissions : null);
  cabaClient.isAdmin = () => (didLoad ? auth.claims.isAdmin : false);
  cabaClient.isInitialized = cabaClient.auth;
  return cabaClient;
};

const defaultState = () => ({
  isInitialized: false,
  apis: {},
  getCustomer: () => null,
  myPermissions: () => ({}),
  isAdmin: () => false,
});

function useServer({ jwtToken }) {
  const [api, setApi] = useState(defaultState());
  const { token: cabaToken, idToken: googleIdToken, isLoggedIn } = useCaba();
  useEffect(() => {
    let isLoggingIn = false;
    if (isLoggedIn && !isLoggingIn) {
      (async () => {
        isLoggingIn = true;
        const client = await new Swagger(`${apiConfig.url}/swagger.json`, {
          authorizations: {
            cabaSchema: cabaToken ? `Bearer ${cabaToken}` : undefined,
            googleSchema: googleIdToken ? `Bearer ${googleIdToken}` : undefined,
            jwtSchema: jwtToken ? `Bearer ${jwtToken}` : undefined,
          },
        });
        const apiClient = await prepareClient(client);
        window.Api = apiClient;
        setApi(apiClient);
        isLoggingIn = false;
      })();
    } else {
      setApi(defaultState());
    }
  }, [cabaToken, googleIdToken, isLoggedIn, jwtToken]);
  return api;
}

export { useServer as default };
